import * as Sentry from '@sentry/vue';
import { defineStore } from 'pinia';
import { v4 as uuid } from 'uuid';
import { createSocket, renderSafeHtmlFromMarkdown } from '@frontend/ui/features/chat/chatFunctions.js';
import { useAdvisorStore } from './advisorStore.js';
import { generateTimestamp } from '@frontend/shared/Util.js';

// It is convention to prefix store names here with "use"
export const useChatStore = defineStore('chat', {
  state: () => ({
    socket: null,
    conversations: {},
    currentConversationId: null,
    selectedConversationId: null,
    activeTabName: '',
    // Tracks in-flight tool operations so UI can show a spinner until completion
    toolActivityCount: 0,
    lastToolDescription: '',
    fetching: false,
    isFetchingConversations: true,
  }),

  getters: {
    latestConversations() {
      return Object.values(this.conversations).sort((a, b) => {
        const aTime = a.updated_at || 0;
        const bTime = b.updated_at || 0;

        return new Date(bTime) - new Date(aTime); // newest first
      });
    },
  },

  actions: {
    // Setup
    async setContext(context) {
      const conversation = this.conversations[this.currentConversationId];
      this.conversations = {
        ...this.conversations,
        [this.currentConversationId]: {
          ...conversation,
          context,
        },
      };
    },
    startToolActivity(description) {
      try {
        this.toolActivityCount = (this.toolActivityCount || 0) + 1;
        if (description) this.lastToolDescription = String(description);
      } catch (e) {
        this.toolActivityCount = 1;
        if (description) this.lastToolDescription = String(description);
      }
    },
    finishToolActivity() {
      try {
        const next = (this.toolActivityCount || 0) - 1;
        this.toolActivityCount = next > 0 ? next : 0;
        if (this.toolActivityCount === 0) this.lastToolDescription = '';
      } catch (e) {
        this.toolActivityCount = 0;
        this.lastToolDescription = '';
      }
    },
    resetToolActivity() {
      this.toolActivityCount = 0;
      this.lastToolDescription = '';
    },
    async createSocket(socketPath = '/ws/agent-chat/', conversationId = null) {
      if (!this.socket) {
        this.socket = await createSocket(this, socketPath, conversationId);
      }

      if (conversationId) {
        this.loadConversation(conversationId);
      } else {
        this.startNewConversation();
      }
    },

    // Messages
    startNewConversation() {
      const id = uuid(); // if you're worried about collision, you should be building a bunker, since comet hitting earth is more likely- source: meme

      const md = [
        'Ask me anything! Here\'s what I can access:',
        '',
        '- Client dossiers',
        '- Meetings',
        '- Portfolios',
        '- Documents',
        '- Risk profiles',
        '- Values set',
        '- Recommendations',
        '- Calculator',
        '- Web search',
        '- Email (only available if you authorize Outlook, Gmail coming soon!)',
        '',
        'Responses may be instant or take up to 10 minutes to complete, depending on how deep I have to search.',
      ].join('\n');
      const text = renderSafeHtmlFromMarkdown(md);
      const timestamp = generateTimestamp();

      this.conversations[id] = {
        id,
        messages: [{
          id: `${timestamp}_assistant`,
          text,
          role: 'assistant',
          timestamp,
        }],
        system_prompt: '',
        context: {},
        streamingMessage: {
          text: '',
          role: 'assistant',
        },
      };
      this.currentConversationId = id;
    },

    async pushMessage(message) {
      const id = this.currentConversationId;
      const conversation = this.conversations[id];

      if (!conversation) return;

      const timestamp = message.timestamp || generateTimestamp();
      const newMessage = { ...message, timestamp };

      // this looks complicated but it's just pushing to the messages array. Have to do it like this because of reactivity
      this.conversations = {
        ...this.conversations,
        [id]: {
          ...conversation,
          messages: [
            ...conversation.messages,
            newMessage,
          ],
        },
      };
    },
    async sendMessage(message, portfolioID, benchmarkID) {
      this.pushMessage(message);
      this.socket.send(JSON.stringify({
        ...this.conversations[this.currentConversationId],
        activeTabName: this.activeTabName,
        portfolio_id: portfolioID,
        benchmark_id: benchmarkID,
      }));
    },
    async setStreamingMessage(message) {
      const conversation = this.conversations[this.currentConversationId];
      this.conversations = {
        ...this.conversations,
        [this.currentConversationId]: {
          ...conversation,
          streamingMessage: message,
        },
      };
    },
    async pushToStreamingMessage(message) {
      const conversation = this.conversations[this.currentConversationId];
      this.conversations = {
        ...this.conversations,
        [this.currentConversationId]: {
          ...conversation,
          streamingMessage: { text: conversation.streamingMessage.text + message.text, role: message.role },
        },
      };
    },

    // Conversation navigation
    async setSelectedConversationId(id) {
      if (!id || id === this.selectedConversationId) return;

      // close existing socket if any
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        try {
          this.socket.close();
        } catch (e) {}
      }
      this.socket = null;

      this.selectedConversationId = id;

      // ensure a conversation placeholder exists
      if (!this.conversations[id]) {
        this.conversations = {
          ...this.conversations,
          [id]: {
            id,
            messages: [],
            system_prompt: '',
            context: {},
            streamingMessage: { text: '', role: 'assistant' },
          },
        };
      }

      this.currentConversationId = id;

      // open socket bound to this conversation
      await this.createSocket('/ws/agent-chat/', id);
    },

    // Server communication
    mergeServerConversation(server) {
      const id = server.conversation_id;

      // Ensure local structure exists
      let local = this.conversations[id];
      if (!local) {
        local = {
          id,
          messages: [],
          system_prompt: '',
          context: {},
          streamingMessage: { text: '', role: 'assistant' },
        };
      }

      // Convert server messages → local shape
      const incomingMessages = (server.messages || []).map(m => ({
        id: m.id ?? `${m.timestamp}_${m.role}`,
        text: m.text,
        role: m.role, // 'user' | 'assistant'
        timestamp: m.timestamp,
      }));

      // Merge + dedupe messages
      const existing = local.messages || [];
      const map = new Map();

      for (const m of existing) map.set(m.id || m.timestamp, m);
      for (const m of incomingMessages) map.set(m.id, m);

      const merged = Array.from(map.values()).sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
      );

      let title = 'No title';

      if (merged && merged.length > 0) {
        const firstUserMessage = merged.find(m => m.role === 'user');

        if (firstUserMessage && firstUserMessage.text.trim() !== '') {
          title = firstUserMessage.text;
        }
      }

      // Write back, preserving local metadata fields
      this.conversations = {
        ...this.conversations,
        [id]: {
          ...local,
          messages: merged,
          title,
          created_at: server.created_at,
          updated_at: server.updated_at,
          entity_name: server.entity_name,
          entity_id: server.entity_id,
        },
      };
    },

    mergeServerMessages(conversationId, serverMessages) {
      const local = this.conversations[conversationId];
      if (!local) return;

      // Merge
      const merged = [...local.messages, ...serverMessages.items];

      // Sort oldest → newest
      merged.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      // Dedupe by timestamp
      const seen = new Set();
      const deduped = [];

      for (const msg of merged) {
        if (!seen.has(msg.timestamp)) {
          seen.add(msg.timestamp);
          deduped.push(msg);
        }
      }

      this.conversations = {
        ...this.conversations,
        [conversationId]: {
          ...local,
          messages: deduped,
        },
      };
    },

    async loadConversation(conversationId) {
      await this.fetchConversationMessages(conversationId);
      this.currentConversationId = conversationId;
    },

    async fetchConversations() {
      this.isFetchingConversations = true;
      const advisorStore = useAdvisorStore();

      try {
        const response = await fetch(`/api/v1/conversations/`, {
          method: 'GET',
          headers: advisorStore.headers,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch conversations: ${response.status}`);
        }

        const data = await response.json();

        if (data.conversations) {
          data.conversations.forEach(serverConvo => {
            this.mergeServerConversation(serverConvo);
          });
        }
      } catch (error) {
        console.error(`Error fetching conversations`, error);
        Sentry.captureException(error);
      } finally {
        this.isFetchingConversations = false;
      }
    },

    async fetchConversationMessages(conversationId, offset = null, limit = 50) {
      this.fetching = true;
      const advisorStore = useAdvisorStore();

      try {
        const params = new URLSearchParams();

        if (limit) {
          params.set('limit', limit);
        }

        if (offset) {
          params.set('offset', offset);
        }

        let query = '';

        if ([...params].length > 0) {
          query = `?${params.toString()}`;
        }

        const response = await fetch(`/api/v1/conversations/${conversationId}/messages/${query}`, {
          method: 'GET',
          headers: advisorStore.headers,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch messages: ${response.status}`);
        }

        const data = await response.json();

        if (data) {
          this.mergeServerMessages(conversationId, data);
        }
      } catch (error) {
        console.error(`Error fetching messages`, error);
        Sentry.captureException(error);
      } finally {
        this.fetching = false;
      }
    },
  },
});
